import { Effect, Schema as S } from "effect";

import { type Reader, defineApi } from "../client";
import { Page } from "../schemas/common";
import {
	type DownloadedFile,
	DownloadedFile as DownloadedFileSchema,
	Entry,
	EntryHistory,
	UploadEntryResponse,
} from "../schemas/entries";

const AsOf = S.NullOr(S.String);

/** v1 `useEntries`: `asOf` set hits the `as_of` form (no search), as the hook did. */
export const getEntries = defineApi("GetEntries", {
	args: { nodeId: S.String, search: S.String, cursor: S.Number, asOf: AsOf },
	response: Page(Entry),
	request: ({ nodeId, search, cursor, asOf }) => ({
		method: "GET",
		path: "/entries",
		query: asOf ? { nodeId, as_of: asOf, cursor } : { nodeId, search, cursor },
	}),
});

/** v1 `useEntry`: `asOf` set hits the `as_of` form. */
export const getEntry = defineApi("GetEntry", {
	args: { knotId: S.String, asOf: AsOf },
	response: Entry,
	request: ({ knotId, asOf }) => ({
		method: "GET",
		path: `/entries/${knotId}`,
		query: { as_of: asOf },
	}),
});

export const addEntry = defineApi("AddEntry", {
	args: { data: S.Unknown },
	response: S.Unknown,
	request: ({ data }) => ({ method: "POST", path: "/entries", body: data }),
});

export const deleteEntry = defineApi("DeleteEntry", {
	args: { knotId: S.String },
	response: S.Unknown,
	request: ({ knotId }) => ({ method: "DELETE", path: `/entries/${knotId}` }),
});

export const editEntry = defineApi("EditEntry", {
	args: { knotId: S.String, entry: Entry },
	response: S.Unknown,
	request: ({ knotId, entry }) => ({
		method: "PATCH",
		path: `/entries/${knotId}`,
		body: entry,
	}),
});

export const uploadEntry = defineApi("UploadEntry", {
	args: {
		nodeId: S.String,
		file: S.instanceOf(File),
		/** ISO instant the upload occurred on; the server ignores it until U1 reads it. */
		timestamp: S.optionalKey(S.String),
	},
	response: UploadEntryResponse,
	request: ({ nodeId, file, timestamp }) => {
		const body = new FormData();
		body.append("nodeId", nodeId);
		body.append("file", file);
		if (timestamp !== undefined) body.append("timestamp", timestamp);
		return { method: "POST", path: "/entries/upload", body };
	},
});

/** v1's `extractFileName`: RFC 5987 `filename*` first, then plain `filename`. */
export const fileNameFromDisposition = (
	value: string | undefined,
): string | undefined => {
	if (!value) return undefined;
	const utf8 = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
	if (utf8) {
		try {
			return decodeURIComponent(utf8);
		} catch {
			// fall through to the ASCII form
		}
	}
	return value.match(/filename="?([^";]+)"?/i)?.[1];
};

const readBlob: Reader<DownloadedFile> = (response) =>
	response.arrayBuffer.pipe(
		Effect.map((buffer) => {
			const contentType = response.headers["content-type"];
			return {
				blob: new Blob([buffer], contentType ? { type: contentType } : {}),
				fileName: fileNameFromDisposition(
					response.headers["content-disposition"],
				),
				contentType,
			};
		}),
	);

export const downloadEntryFile = defineApi("DownloadEntryFile", {
	args: { knotId: S.String, fileId: AsOf },
	response: DownloadedFileSchema,
	read: readBlob,
	request: ({ knotId, fileId }) => ({
		method: "GET",
		path: `/entries/${knotId}/download`,
		query: { fileId },
	}),
});

export const getEntryHistory = defineApi("GetEntryHistory", {
	args: { knotId: S.String, cursor: S.Number },
	response: EntryHistory,
	request: ({ knotId, cursor }) => ({
		method: "GET",
		path: `/entries/${knotId}/history`,
		query: { cursor, limit: 20 },
	}),
});
